import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { endPoints } from '../../Config';
import usePreferredLanguage from '../../utilities/usePreferredLanguage';
import { translateUiText } from '../../utilities/siteTranslations';

const AddEditUser = ({ editable, viewable, onHide, rowRecord, onClose }) => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [roleOptions, setRoleOptions] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [loadingBtn, setLoadingBtn] = useState(false);

    const dispatch = useDispatch();
    const currentUser = JSON.parse(localStorage.getItem('userDetails') || localStorage.getItem('adminUser') || '{}');
    const currentAdmin = currentUser?.data?.adminUser || {};
    const currentRoleUid = currentAdmin?.role?.uid;

    const passwordValidation = Yup.string()
        .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing spaces are not allowed'))
        .min(8, t('Password must be at least 8 characters long'))
        .matches(/[A-Z]/, t('Password must contain at least one uppercase letter'))
        .matches(/[a-z]/, t('Password must contain at least one lowercase letter'))
        .matches(/[0-9]/, t('Password must contain at least one number'));

    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('This field is required'))
            .matches(/^[a-zA-Z0-9\s]*$/, t('Name must contain only alphanumeric values')),
        lastName: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('This field is required'))
            .matches(/^[a-zA-Z0-9\s]*$/, t('Name must contain only alphanumeric values')),
        whatsApp: Yup.string()
            .matches(/^\d+$/, t('Phone number should only contain numbers'))
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('This field is required')),
        email: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .max(254, t('Email is too long'))
            .email(t('Invalid email'))
            .required(t('This field is required')),
        role: Yup.mixed().required(t('Role is required')),
        parlevelDriverId: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .integer(t('Parlevel driver ID must be an integer'))
            .positive(t('Parlevel driver ID must be positive')),
        parlevelDriverName: Yup.string().trim().max(120, t('Parlevel driver name is too long')),
        teamId: Yup.mixed().test('team-required-if-not-admin', t('Team is required for non-admin roles'), function (value) {
            const { role } = this.parent || {};
            const selectedRole = roleOptions.find((r) => r?._id === role);
            const isAdmin = selectedRole?.title?.toLowerCase() === 'admin';

            if (isAdmin) {
                return true; // not required when admin
            }

            return !!value;
        }),
        password: editable
            ? passwordValidation.notRequired()
            : passwordValidation
                  .matches(/[@$!%*?&^#_~]/, t('Password must contain at least one special character'))
                  .required(t('This field is required'))
    });

    const formik = useFormik({
        validationSchema,
        initialValues: {
            userId: '',
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            whatsApp: '',
            teamId: '',
            role: '',
            canReviewOperatorAnomalies: false,
            parlevelDriverId: '',
            parlevelDriverName: ''
        },
        onSubmit: async (data) => {
            setLoadingBtn(true);

            const selectedRole = roleOptions.find((role) => role?._id === data?.role);
            const isAdminSelectedOnSubmit = selectedRole?.title?.toLowerCase() === 'admin';
            const teamIdToSend = isAdminSelectedOnSubmit ? undefined : data?.teamId;
            const canManageOperatorAnomalyReviewPermission = currentRoleUid === 'admin' || currentRoleUid === 'superAdmin';

            const addData = {
                firstName: data?.firstName,
                lastName: data?.lastName,
                email: data?.email,
                password: data?.password,
                whatsApp: data?.whatsApp,
                role: data?.role,
                teamId: teamIdToSend
            };
            if (canManageOperatorAnomalyReviewPermission) {
                addData.canReviewOperatorAnomalies = Boolean(data?.canReviewOperatorAnomalies);
                addData.parlevelDriverId = data?.parlevelDriverId || null;
                addData.parlevelDriverName = data?.parlevelDriverName?.trim() || null;
            }

            const editData = {
                userId: data.userId,
                firstName: data?.firstName,
                lastName: data?.lastName,
                email: data?.email,
                whatsApp: data?.whatsApp,
                role: data?.role,
                teamId: teamIdToSend
            };
            if (canManageOperatorAnomalyReviewPermission) {
                editData.canReviewOperatorAnomalies = Boolean(data?.canReviewOperatorAnomalies);
                editData.parlevelDriverId = data?.parlevelDriverId || null;
                editData.parlevelDriverName = data?.parlevelDriverName?.trim() || null;
            }

            if (data?.password) {
                editData.password = data.password;
            }

            if (!editable) {
                const response = await dispatch(handlePostRequest(addData, endPoints.addUser, true));
                if (response?.status === 201) {
                    formik.resetForm();
                    onHide();
                }
                setLoadingBtn(false);
            } else {
                const response = await dispatch(handlePutRequest(editData, endPoints.updateUser, true));
                if (response?.status === 200) {
                    formik.resetForm();
                    onHide();
                }
                setLoadingBtn(false);
            }
        }
    });

    const getRoleOptions = async () => {
        const response = await handleGetRequest(endPoints.getRoleDropdown, true);
        let roles = response?.data || [];

        // If current user is plain admin, hide the Admin role from dropdown (can’t create peers)
        if (currentRoleUid === 'admin') {
            roles = roles.filter((r) => (r.uid || r.title?.toLowerCase()) !== 'admin');
        }

        setRoleOptions(roles);
    };
    const getTeamsList = async () => {
        const response = await handleGetRequest(endPoints.getallteams, true, false, dispatch);
        if (response) {
            setAllTeams(response?.data);
        }
    };
    useEffect(() => {
        getTeamsList();
        getRoleOptions();
    }, []);

    const getUserById = async () => {
        setLoadingBtn(true);
        const response = await handleGetRequest(endPoints.getUserById(rowRecord._id), true);
        if (response) {
            formik.setFieldValue('userId', response?.data?._id);
            formik.setFieldValue('firstName', response?.data?.firstName);
            formik.setFieldValue('lastName', response?.data?.lastName);
            formik.setFieldValue('whatsApp', response?.data?.whatsApp);
            formik.setFieldValue('email', response?.data?.email);
            formik.setFieldValue('teamId', response?.data?.teamId);
            formik.setFieldValue('role', response?.data?.role);
            formik.setFieldValue('canReviewOperatorAnomalies', Boolean(response?.data?.canReviewOperatorAnomalies));
            formik.setFieldValue('parlevelDriverId', response?.data?.parlevelDriverId || '');
            formik.setFieldValue('parlevelDriverName', response?.data?.parlevelDriverName || '');
        }
        setLoadingBtn(false);
    };

    useEffect(() => {
        if (editable || viewable) {
            getUserById();
        }
    }, [rowRecord]);

    const selectedRole = roleOptions.find((role) => role?._id === formik?.values?.role);
    const isAdminSelected = selectedRole?.title?.toLowerCase() === 'admin';

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className="grid p-fluid">
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="firstName">
                            {t('First Name')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="firstName" name="firstName" placeholder={t('Enter First Name')} value={formik?.values?.firstName} maxLength={30} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.firstName && formik.errors.firstName && <small className="p-error">{formik.errors.firstName}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="lastName">
                            {t('Last Name')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="lastName" name="lastName" placeholder={t('Enter Last Name')} value={formik?.values?.lastName} maxLength={30} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.lastName && formik.errors.lastName && <small className="p-error">{formik.errors.lastName}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="email">
                            {t('Email')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="email" name="email" placeholder={t('Enter Email')} value={formik?.values?.email} maxLength={254} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.email && formik.errors.email && <small className="p-error">{formik.errors.email}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="whatsApp">
                            {t('Phone')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="whatsApp" name="whatsApp" placeholder={t('Enter Phone')} value={formik?.values?.whatsApp} maxLength={13} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.whatsApp && formik.errors.whatsApp && <small className="p-error">{formik.errors.whatsApp}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="role">
                            {t('Role')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown id="role" name="role" value={formik?.values?.role} options={roleOptions} optionLabel="title" optionValue="_id" onChange={formik.handleChange} placeholder={t('Select Role')} className="w-full" disabled={viewable} />
                        {formik.touched.role && formik.errors.role && <small className="p-error">{formik.errors.role}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="teamId">
                            {t('Team')} <span style={{ color: 'red' }}></span>
                        </label>
                        <Dropdown
                            id="teamId"
                            name="teamId"
                            value={formik?.values?.teamId}
                            options={allTeams}
                            optionLabel="name"
                            optionValue="_id"
                            onChange={formik.handleChange}
                            placeholder={t('Select Team')}
                            className="w-full"
                            disabled={viewable || isAdminSelected}
                        />
                        {formik.touched.teamId && formik.errors.teamId && <small className="p-error">{formik.errors.teamId}</small>}
                    </div>
                </div>
                {!viewable && (
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="password">
                                {t('Password')} <span style={{ color: 'red' }}>*</span>
                            </label>
                            <InputText id="password" name="password" placeholder={t('Enter Password')} value={formik?.values?.password} maxLength={30} onChange={formik.handleChange} />
                            {formik.touched.password && formik.errors.password && <small className="p-error">{formik.errors.password}</small>}
                        </div>
                    </div>
                )}
                {(currentRoleUid === 'admin' || currentRoleUid === 'superAdmin') && (
                    <>
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="parlevelDriverId">{t('Parlevel driver ID')}</label>
                                <InputText id="parlevelDriverId" name="parlevelDriverId" inputMode="numeric" value={formik.values.parlevelDriverId} onChange={formik.handleChange} disabled={viewable} />
                                {formik.touched.parlevelDriverId && formik.errors.parlevelDriverId && <small className="p-error">{formik.errors.parlevelDriverId}</small>}
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="parlevelDriverName">{t('Parlevel driver name')}</label>
                                <InputText id="parlevelDriverName" name="parlevelDriverName" value={formik.values.parlevelDriverName} maxLength={120} onChange={formik.handleChange} disabled={viewable} />
                                {formik.touched.parlevelDriverName && formik.errors.parlevelDriverName && <small className="p-error">{formik.errors.parlevelDriverName}</small>}
                                <small className="text-color-secondary block mt-1">{t('Use the exact driver identity shown in Parlevel.')}</small>
                            </div>
                        </div>
                    <div className="col-12 md:col-6">
                        <div className="field mb-3">
                            <label htmlFor="canReviewOperatorAnomalies">{t('Can review operator anomalies')}</label>
                            <div className="flex align-items-center gap-2">
                                <InputSwitch
                                    id="canReviewOperatorAnomalies"
                                    name="canReviewOperatorAnomalies"
                                    checked={Boolean(formik.values.canReviewOperatorAnomalies)}
                                    onChange={(event) => formik.setFieldValue('canReviewOperatorAnomalies', Boolean(event.value))}
                                    disabled={viewable}
                                />
                                <span className="text-sm text-color-secondary">{t('Shows anomaly approvals tab')}</span>
                            </div>
                        </div>
                    </div>
                    </>
                )}
            </div>

            <div className="flex justify-content-center mt-4 gap-2">
                {!viewable && (
                    <Button label={editable ? t('Update') : t('Save')} icon={editable ? 'pi pi-pencil' : 'pi pi-save'} type="submit" disabled={loadingBtn} className="p-button-success p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} />
                )}
                <Button label={t('Cancel')} icon="pi pi-times" type="button" className="p-button-secondary p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} onClick={() => onClose()} />
            </div>
        </form>
    );
};

export default AddEditUser;
